import { useRef } from 'react'
import type { MouseEvent } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@mui/system'
import { useTranslation } from 'react-i18next'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip
} from 'chart.js'
import type { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js'
import { Radar, getElementAtEvent } from 'react-chartjs-2'

import { Department } from 'src/graphql/types'
import type { ProgressionReportFieldsFragment } from 'src/graphql/types'
import { scoreThreshold } from 'src/screens/match/assessments/score/Score'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Tooltip)

// Grades render on the same 0..10 scale as the rest of the page. The radial
// axis shows all 11 ticks (0 at the center, 10 at the rim); grades are 1..10
// inclusive, so points never actually land on the center.
const maxScore = 10

// A radar needs at least three axes to read as a web rather than a line/point.
const minDepartments = 3

// Opacity of the colored department wedges.
const sectorAlpha = 0.6

// Radius (px) of an individual assessment dot; shared by the dataset and the
// overlap-jitter math so they can't drift apart.
const dotRadius = 4

// Converts a stored 0..1 grade to the 0..10 display scale (1..10 inclusive),
// keeping one decimal so individual dots land on tenths, not integers.
const toDisplayScore = (score: number) =>
  Math.max(Math.round(score * 100) / 10, 1)

type Progression = ProgressionReportFieldsFragment['assignmentProgressions'][0]

// One plotted assessment: which department spoke it sits on, its 1..10 grade,
// and the id used to scroll to its card below.
type Assessment = {
  deptIndex: number
  value: number
  id: string
  title: string
}

type RadarModel = {
  labels: string[]
  averages: number[]
  assessments: Assessment[]
}

// The subset of RadialLinearScale internals the wedge plugin needs. These are
// real runtime methods on the radar scale but aren't in the public typings.
type RadialScale = {
  xCenter: number
  yCenter: number
  drawingArea: number
  getIndexAngle: (index: number) => number
  getDistanceFromCenterForValue: (value: number) => number
}

// Shared anchor id so the radar can scroll to the matching assignment card.
export const assignmentAnchorId = (id: string) => `assessment-${id}`

export function SkillRadar(props: {
  data?: ProgressionReportFieldsFragment
  hasError: boolean
  shouldRender: boolean
  // When set, the whole card is a click-through (e.g. to the profile page) and
  // per-dot scroll-to-assessment is disabled.
  onClick?: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const gradient = useGradientColor()
  const chartRef = useRef(null)

  const { data, hasError, shouldRender } = props
  const clickable = Boolean(props.onClick)
  if (!data || hasError || !shouldRender) {
    return null
  }

  const model = buildModel(data.assignmentProgressions, t)
  if (model.labels.length < minDepartments) {
    return null
  }

  const { labels, averages, assessments } = model

  // Dataset layout (also the dataset index used by click/tooltip handlers):
  //   0..N-1 -> one point per individual assessment
  //   N      -> the bigger average points, drawn on top
  const individualDatasets = assessments.map((assessment) => {
    const color = gradient(assessment.value)
    return {
      label: 'assessment',
      data: averages.map((_, index) =>
        index === assessment.deptIndex ? assessment.value : null
      ),
      showLine: false,
      fill: false,
      pointRadius: dotRadius,
      pointHoverRadius: dotRadius + 3,
      pointBackgroundColor: color,
      pointBorderColor: color
    }
  })

  const averageDataset = {
    label: 'average',
    data: averages,
    showLine: false,
    fill: false,
    pointRadius: 8,
    pointHoverRadius: 10,
    pointBackgroundColor: averages.map((value) => gradient(value)),
    pointBorderColor: theme.d.colors.text.secondary,
    pointBorderWidth: 2
  }

  const chartData: ChartData<'radar', (number | null)[], string> = {
    labels,
    datasets: [...individualDatasets, averageDataset]
  }

  const gridColor = theme.d.colors.border.divider
  const labelColor = theme.d.colors.text.secondary

  // datasetIndex of an individual assessment maps straight back to assessments.
  const assessmentFor = (datasetIndex: number) =>
    datasetIndex >= 0 && datasetIndex < assessments.length
      ? assessments[datasetIndex]
      : undefined

  // Fills each department's angular slice with a solid color set by its average
  // grade, drawn under the grid/points so weak (red) and strong (green)
  // departments are obvious at a glance. Adjacent slices abut, so the color
  // change between them is sharp.
  const sectorsPlugin: Plugin<'radar'> = {
    id: 'departmentSectors',
    beforeDraw(chart) {
      const scale = chart.scales.r as unknown as RadialScale | undefined
      if (!scale?.drawingArea) return
      const count = averages.length
      if (count === 0) return

      const { ctx } = chart
      const step = (2 * Math.PI) / count

      averages.forEach((average, index) => {
        const center = scale.getIndexAngle(index) - Math.PI / 2
        const radius = scale.getDistanceFromCenterForValue(average)

        ctx.save()
        ctx.beginPath()
        ctx.moveTo(scale.xCenter, scale.yCenter)
        ctx.arc(
          scale.xCenter,
          scale.yCenter,
          radius,
          center - step / 2,
          center + step / 2
        )
        ctx.closePath()
        ctx.fillStyle = withAlpha(gradient(average), sectorAlpha)
        ctx.fill()
        // Thin separator in the panel color crisply divides neighboring slices.
        ctx.lineWidth = 1.5
        ctx.strokeStyle = theme.d.colors.panel.light
        ctx.stroke()
        ctx.restore()
      })
    },
    // Fan out individual dots that would otherwise stack on the same spot so
    // every assessment stays visible. Averages are left untouched, so they stay
    // centered on each spoke.
    afterDatasetsUpdate(chart) {
      const scale = chart.scales.r as unknown as RadialScale | undefined
      if (!scale?.drawingArea) return
      const count = averages.length
      if (count === 0) return

      const step = (2 * Math.PI) / count
      const minSeparation = dotRadius * 2 + 1

      const datasetsByDept = new Map<number, number[]>()
      assessments.forEach((assessment, datasetIndex) => {
        const list = datasetsByDept.get(assessment.deptIndex) ?? []
        list.push(datasetIndex)
        datasetsByDept.set(assessment.deptIndex, list)
      })

      datasetsByDept.forEach((datasetIndexes, deptIndex) => {
        const baseAngle = scale.getIndexAngle(deptIndex) - Math.PI / 2
        const points = datasetIndexes
          .map(
            (datasetIndex) =>
              chart.getDatasetMeta(datasetIndex).data[deptIndex] as unknown as
                | { x: number; y: number }
                | undefined
          )
          .filter((element): element is { x: number; y: number } =>
            Boolean(element)
          )
          .map((element) => ({
            element,
            distance: Math.hypot(
              element.x - scale.xCenter,
              element.y - scale.yCenter
            )
          }))
          .sort((a, b) => a.distance - b.distance)

        // Split the spoke into clusters of radially-overlapping dots, then fan
        // each cluster; isolated dots fall in clusters of one and stay put.
        let clusterStart = 0
        for (let index = 1; index <= points.length; index++) {
          const isBoundary =
            index === points.length ||
            points[index].distance - points[index - 1].distance > minSeparation
          if (!isBoundary) continue
          fanCluster(
            points.slice(clusterStart, index),
            baseAngle,
            step,
            scale,
            minSeparation
          )
          clusterStart = index
        }
      })
    }
  }

  const options: ChartOptions<'radar'> = {
    animation: false,
    maintainAspectRatio: false,
    layout: { padding: 8 },
    scales: {
      r: {
        min: 0,
        max: maxScore,
        beginAtZero: true,
        // Keep a ring at every integer, but hide the numeric labels.
        ticks: {
          stepSize: 1,
          display: false
        },
        grid: { color: gridColor, circular: true },
        angleLines: { display: false },
        pointLabels: {
          color: labelColor,
          font: {
            size: 15,
            family: theme.d.font.primary
          }
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'radar'>[]) =>
            items[0] ? labels[items[0].dataIndex] : '',
          label: (ctx: TooltipItem<'radar'>) => {
            if (ctx.datasetIndex === assessments.length) {
              const rounded = Math.round(ctx.parsed.r * 10) / 10
              return ` ${t(
                'assessments:radar:average'
              )}: ${rounded}/${maxScore}`
            }
            const assessment = assessmentFor(ctx.datasetIndex)
            return assessment
              ? ` ${assessment.title}: ${assessment.value}/${maxScore}`
              : ''
          }
        }
      }
    },
    onHover: clickable
      ? undefined
      : (event, elements) => {
          const target = (event.native?.target ?? null) as HTMLElement | null
          if (!target?.style) return
          const overAssessment = elements.some(
            (element) => assessmentFor(element.datasetIndex) != null
          )
          target.style.cursor = overAssessment ? 'pointer' : 'default'
        }
  }

  const handleClick = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current) return
    const element = getElementAtEvent(chartRef.current, event)[0]
    if (!element) return
    const assessment = assessmentFor(element.datasetIndex)
    if (!assessment) return
    scrollToAssessment(assessment.id)
  }

  return (
    <Container
      clickable={clickable}
      onClick={props.onClick}
      title={clickable ? t('assessments:radar:view_profile') : undefined}
    >
      <Title>{t('assessments:radar:title')}</Title>
      <NarrowMessage>{t('assessments:radar:needs_wider')}</NarrowMessage>
      <ChartArea>
        <Radar
          ref={chartRef}
          data={chartData}
          options={options}
          plugins={[sectorsPlugin]}
          onClick={clickable ? undefined : handleClick}
        />
      </ChartArea>
      {clickable && (
        <ClickHint>{t('assessments:radar:view_profile')} →</ClickHint>
      )}
    </Container>
  )
}

function scrollToAssessment(id: string) {
  const anchor = assignmentAnchorId(id)
  const node = document.getElementById(anchor)
  if (!node) return
  node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  globalThis.history?.replaceState?.(null, '', `#${anchor}`)
}

// Groups the (already-rendered) assignment progressions by department. Each
// department becomes a slice holding every individual assessment plus the
// average of those assessments. Only departments that actually have data end up
// as slices, so the radar mirrors whatever the page renders.
function buildModel(
  progressions: Progression[],
  t: (key: string, opts?: object) => string
): RadarModel {
  const byDepartment = new Map<Department, Assessment[]>()

  for (const progression of progressions) {
    const score = progression.assignment?.grade?.score
    if (score == null) continue
    const list = byDepartment.get(progression.department) ?? []
    list.push({
      // deptIndex is filled in once the axis order is known, below.
      deptIndex: -1,
      value: toDisplayScore(score),
      id: progression.id,
      title: progression.assignment?.title?.text ?? ''
    })
    byDepartment.set(progression.department, list)
  }

  // Iterate the enum so axis order is stable regardless of API ordering.
  const departments = Object.values(Department).filter((department) =>
    byDepartment.has(department)
  )

  const labels: string[] = []
  const averages: number[] = []
  const assessments: Assessment[] = []

  departments.forEach((department, deptIndex) => {
    const list = byDepartment.get(department) as Assessment[]
    labels.push(departmentLabel(department, t))
    averages.push(list.reduce((acc, item) => acc + item.value, 0) / list.length)
    for (const item of list) {
      assessments.push({ ...item, deptIndex })
    }
  })

  return { labels, averages, assessments }
}

function departmentLabel(
  department: Department,
  t: (key: string, opts?: object) => string
) {
  return t(`assessments:department:${department}:title`, {
    defaultValue: department
  })
}

// Color thresholds reuse the page's score buckets (Score.tsx): at/below the
// medium threshold is pure red, the high threshold is pure yellow, and 10 is
// pure green.
const RED_MAX = scoreThreshold.medium
const YELLOW_AT = scoreThreshold.high

// Builds an aggressive red -> yellow -> green gradient on the theme colors:
// flat red up to RED_MAX, a red->yellow fade from RED_MAX to YELLOW_AT, then a
// yellow->green fade from YELLOW_AT to the rim (so green only shows up high).
function useGradientColor() {
  const theme = useTheme()
  const red = hexToRgb(theme.d.colors.error)
  const yellow = hexToRgb(theme.d.colors.warning)
  const green = hexToRgb(theme.d.colors.success)
  const clamp01 = (t: number) => Math.max(0, Math.min(1, t))

  return (score: number) => {
    const clamped = Math.max(0, Math.min(maxScore, score))
    if (clamped <= YELLOW_AT) {
      return mix(
        red,
        yellow,
        clamp01((clamped - RED_MAX) / (YELLOW_AT - RED_MAX))
      )
    }
    return mix(
      yellow,
      green,
      clamp01((clamped - YELLOW_AT) / (maxScore - YELLOW_AT))
    )
  }
}

type Rgb = { r: number; g: number; b: number }

function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  }
}

const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t)

function mix(a: Rgb, b: Rgb, t: number) {
  return `rgb(${lerp(a.r, b.r, t)}, ${lerp(a.g, b.g, t)}, ${lerp(a.b, b.b, t)})`
}

function withAlpha(rgb: string, alpha: number) {
  return rgb.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
}

type JitterPoint = { element: { x: number; y: number }; distance: number }

// Spreads a cluster of overlapping dots symmetrically around their spoke,
// changing only the angle (the radius/value is preserved) and keeping the fan
// inside the slice so dots never bleed into a neighboring department.
function fanCluster(
  cluster: JitterPoint[],
  baseAngle: number,
  step: number,
  center: { xCenter: number; yCenter: number },
  minSeparation: number
) {
  const size = cluster.length
  if (size < 2) return

  const meanDistance =
    cluster.reduce((acc, point) => acc + point.distance, 0) / size
  const maxSpread = step * 0.8
  const deltaAngle = Math.min(
    minSeparation / Math.max(meanDistance, 1),
    maxSpread / (size - 1)
  )

  cluster.forEach((point, index) => {
    const angle = baseAngle + (index - (size - 1) / 2) * deltaAngle
    point.element.x = center.xCenter + Math.cos(angle) * point.distance
    point.element.y = center.yCenter + Math.sin(angle) * point.distance
  })
}

const Container = styled.div<{ clickable?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background: ${(props) => props.theme.d.colors.panel.light};
  border: 1px solid ${(props) => props.theme.d.colors.border.dark};
  border-radius: 4px;
  padding: ${(props) => props.theme.d.spacing[5]};
  margin-bottom: ${(props) => props.theme.d.spacing[8]};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${(props) =>
      props.clickable
        ? props.theme.d.colors.success
        : props.theme.d.colors.border.dark};
  }
`

const Title = styled.h4`
  color: ${(props) => props.theme.d.colors.text.secondary};
  font-size: 20px;
  text-align: center;
  margin: ${(props) => `0 0 ${props.theme.d.spacing[3]}`};
`

const ClickHint = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[3]};
  text-align: center;
  font-size: 14px;
  color: ${(props) => props.theme.d.colors.link.default};
`

// The radar needs room for ~12 spokes and their labels; below the small
// breakpoint it gets too cramped, so we hide it and show NarrowMessage instead.
const ChartArea = styled.div`
  display: none;
  position: relative;
  width: 100%;
  height: 520px;

  @media (min-width: ${(props) => props.theme.d.breakpoints.sm}) {
    display: block;
  }

  @media (min-width: ${(props) => props.theme.d.breakpoints.md}) {
    height: 600px;
  }
`

const NarrowMessage = styled.p`
  margin: 0;
  padding: ${(props) => props.theme.d.spacing[8]} 0;
  text-align: center;
  color: ${(props) => props.theme.d.colors.text.primary};

  @media (min-width: ${(props) => props.theme.d.breakpoints.sm}) {
    display: none;
  }
`
